import React, { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { registerWithEmail, loginWithEmail, getMe } from "../api/authApi";
import { useAuth } from "../auth/AuthProvider";
import { migrateGuestData, clearGuestData } from "../utils/userStorage";
import GoogleSignInButton from "./GoogleSignInButton";

function isDuplicateRegistrationError(err) {
  const status = Number(err?.status || 0);
  const message = (err?.message || "").toString().toLowerCase();
  return status === 400 && (
    message.includes("already") ||
    message.includes("registered") ||
    message.includes("exists")
  );
}

export default function Signup() {
  const navigate = useNavigate();
  const auth = useAuth();
  const setUser = auth?.setUser;

  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const emailOk = useMemo(() => {
    const e = email.trim();
    if (!e) return false;
    return e.includes("@") && e.includes(".");
  }, [email]);

  const pwValue = password.trim();
  const pwOk = pwValue.length >= 8 && /[a-zA-Z]/.test(pwValue) && /[0-9]/.test(pwValue);
  const matchOk = confirm.trim() !== "" && confirm === password;

  const canSubmit = fullName.trim() !== "" && emailOk && pwOk && matchOk;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!canSubmit) {
      setError("Please fill in all fields correctly.");
      return;
    }

    setIsLoading(true);

    try {
      try {
        await registerWithEmail(email.trim(), password);
      } catch (regErr) {
        if (isDuplicateRegistrationError(regErr)) {
          setError("An account with this email already exists. Sign in or reset your password instead.");
          return;
        }
        throw regErr;
      }

      try {
        await loginWithEmail(email.trim(), password);
      } catch (loginErr) {
        // Registration worked but auto-login failed — send to login page so user can sign in manually
        navigate("/login", { replace: true });
        return;
      }

      try {
        const me = await getMe();
        if (me) {
          migrateGuestData(me);
          clearGuestData();
          const onboarded = Boolean(me?.onboarding_complete ?? me?.onboardingComplete);
          if (typeof setUser === "function") setUser(me);
          navigate(onboarded ? "/dashboard" : "/onboarding", { replace: true });
          return;
        }
      } catch {}

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.message || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="onboarding onboardingPage">
      <div className="brandBar">
        <div className="brandLeft">
          <div className="brandMark">
            <img className="brandLogo" src="/officialLogo.png" alt="FitGPT official logo" />
          </div>
        </div>
      </div>

      <div className="card authCard">
        <div className="authHeader">
          <div>
            <h1 className="authTitle">Create account</h1>
            <p className="authSub">Save your wardrobe, favorites, and outfit picks.</p>
          </div>

          <button type="button" className="btn" onClick={() => navigate("/dashboard")}>
            Back
          </button>
        </div>

        <GoogleSignInButton />

        <div className="authDivider"><span>or</span></div>

        <form onSubmit={onSubmit} className="authForm">
          <label className="authFormGroup">
            Full name
            <input
              className="wardrobeInput"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              autoComplete="name"
            />
          </label>

          <label className="authFormGroup">
            Date of birth (optional)
            <input
              className="wardrobeInput"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
            />
          </label>

          <label className="authFormGroup">
            Email
            <input
              className="wardrobeInput"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              inputMode="email"
              autoComplete="email"
            />
          </label>

          <label className="authFormGroup">
            Password
            <div className="authPasswordRow">
              <input
                className="wardrobeInput"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="wardrobeIconBtn authShowBtn"
                onClick={() => setShowPw((v) => !v)}
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            <span className="authHint">At least 8 characters, with a letter and a number.</span>
          </label>

          <label className="authFormGroup">
            Confirm password
            <div className="authPasswordRow">
              <input
                className="wardrobeInput"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="wardrobeIconBtn authShowBtn"
                onClick={() => setShowConfirm((v) => !v)}
              >
                {showConfirm ? "Hide" : "Show"}
              </button>
            </div>
          </label>

          {error ? <div className="authError">{error}</div> : null}

          <button
            type="submit"
            className="btn primary authSubmit"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? "Creating..." : "Create account"}
          </button>

          <div className="authFooter">
            <span className="heroSub">Already have an account?</span>{" "}
            <NavLink to="/login" className="linkLike">
              Sign in
            </NavLink>
          </div>
        </form>
      </div>
    </div>
  );
}
